import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { Box, Chip, Link, Stack, Tab, Tabs, Typography } from "@mui/material";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { PORTAL } from "@accredo/fe-shared/theme/portals.js";

import ContractClaimsCard from "../components/ContractClaimsCard.jsx";
import { Card, DataRow } from "../components/DetailCard.jsx";
import ContractObjectsTable from "../components/ContractObjectsTable.jsx";
import PortalDocumentsTable from "../components/PortalDocumentsTable.jsx";

const EMPTY = "–";

// The name the contract list shows for the same Vertrag: the Objekt's own
// name when the user sees exactly one Objekt of it, otherwise the number.
function contractName(vertrag) {
  const objekte = vertrag.objekts || [];
  return objekte.length === 1 ? objekte[0].bezeichnung : vertrag.vertragsnummer;
}

function insuranceLocation(vertrag) {
  const objekte = vertrag.objekts || [];
  if (objekte.length > 1) {
    return `${objekte.length} Objekte`;
  }
  if (objekte.length === 0) {
    return EMPTY;
  }
  const { strasse, hausnr, plz, ort } = objekte[0];
  const street = [strasse, hausnr].filter(Boolean).join(" ");
  const town = [plz, ort].filter(Boolean).join(" ");
  return [street, town].filter(Boolean).join(", ") || EMPTY;
}

const DATA_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
  columnGap: 3,
  rowGap: 2.5,
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
};

function StatusChip({ vertrag }) {
  return (
    <Chip
      size="small"
      label={vertrag.is_active ? "aktiv" : "archiviert"}
      variant={
        vertrag.is_active
          ? "contract-status_aktiv"
          : "contract-status_archiviert"
      }
    />
  );
}

// Each person starts its own grid row, so a name and its Kontakt stay paired.
function ContactBlock({ label, person, vertrag }) {
  if (!person) {
    return (
      <Box sx={{ gridColumn: { sm: 1 } }}>
        <DataRow label={label}>{EMPTY}</DataRow>
      </Box>
    );
  }

  const subject = encodeURIComponent(
    `${contractName(vertrag)} · ${vertrag.vertragsnummer}`
  );

  return (
    <>
      <Box sx={{ gridColumn: { sm: 1 } }}>
        <DataRow label={label}>{person.full_name}</DataRow>
      </Box>
      <DataRow label="Kontakt">
        {person.email ? (
          <Link href={`mailto:${person.email}?subject=${subject}`}>
            {person.email}
          </Link>
        ) : (
          EMPTY
        )}
      </DataRow>
    </>
  );
}

/**
 * The detail page of one Vertrag: header, Vertragsdaten and the documents
 * card. Loaded through the same scoped repository as the list, so a Vertrag
 * the user may not see answers 404 like one that does not exist.
 */
export default function ContractDetailPage() {
  const { id } = useParams();
  const { user } = useUser();
  const { setBreadcrumbs } = useBreadcrumb();
  const showClaimsCard = (user?.portals ?? []).includes(PORTAL.SCHADENPORTAL);
  const [vertrag, setVertrag] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [searchParams, setSearchParams] = useSearchParams();
  const [section, setSection] = useState("dokumente");
  const [objektSelection, setObjektSelection] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    setVertrag(null);

    backendGet(`/data/vertrags/${id}`, {
      label: "Vertrag",
      options: {
        params: {
          range: "all",
          include: [
            "sachbearbeiter",
            "betreuer",
            "objekts",
            "display_sparten",
            "letzter_nachtrag",
          ],
        },
      },
      onError: (error) => {
        if (!cancelled) {
          setLoadState(error?.response?.status === 404 ? "notfound" : "error");
        }
      },
    }).then((result) => {
      if (cancelled || !result) {
        return;
      }
      setVertrag(result.data);
      setLoadState("loaded");
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let last_step = null;
    if (loadState === "loading") {
      last_step = "Laden …";
    } else if (loadState === "loaded") {
      last_step = contractName(vertrag);
    }

    setBreadcrumbs([
      { name: "Übersicht", path: "/" },
      { name: "Verträge", path: "/vertraege" },
      ...(last_step ? [{ name: last_step, path: `/vertraege/${id}` }] : []),
    ]);
  }, [id, loadState, vertrag, setBreadcrumbs]);

  if (loadState === "loading") {
    return <Typography color="text.secondary">Wird geladen …</Typography>;
  }
  if (loadState === "notfound") {
    return <Typography variant="h2">Vertrag nicht gefunden.</Typography>;
  }
  if (loadState === "error") {
    return (
      <Typography color="error">
        Der Vertrag konnte nicht geladen werden.
      </Typography>
    );
  }

  const objekte = vertrag.objekts || [];
  const showObjekteTab = objekte.length > 0;
  const requestedObjekt = searchParams.get("objekt");
  // An address naming an Objekt the user does not see is ignored.
  const objektFilter =
    objekte.find((entry) => String(entry.id) === requestedObjekt) ?? null;

  const setObjektParam = (objekt_id) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (objekt_id) {
          next.set("objekt", objekt_id);
        } else {
          next.delete("objekt");
        }
        return next;
      },
      { replace: true }
    );

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h1">{contractName(vertrag)}</Typography>
          <StatusChip vertrag={vertrag} />
        </Stack>
        <Typography color="text.secondary">
          {`Vertragsnummer ${vertrag.vertragsnummer} · ${vertrag.vertragsart}`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: showClaimsCard
            ? { xs: "1fr", md: "1.3fr 1fr" }
            : "1fr",
          gap: 3,
          alignItems: "start",
        }}
      >
        <Card title="Vertragsdaten">
          <Box sx={DATA_GRID_SX}>
            <DataRow label="Vertragsnummer">{vertrag.vertragsnummer}</DataRow>
            <DataRow label="Sparte">
              {(vertrag.sparten || []).length ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {vertrag.sparten.map((sparte) => (
                    <Chip key={sparte.id} size="small" label={sparte.sparte} />
                  ))}
                </Stack>
              ) : (
                EMPTY
              )}
            </DataRow>
            <DataRow label="Status">
              <StatusChip vertrag={vertrag} />
            </DataRow>
            <DataRow label="Vertragsbeginn">{getDatum(vertrag.beginn)}</DataRow>
            <DataRow label="Letzter Nachtrag">
              {vertrag.letzter_nachtrag
                ? getDatum(vertrag.letzter_nachtrag)
                : EMPTY}
            </DataRow>
            <DataRow label="Versicherungsort">
              {insuranceLocation(vertrag)}
            </DataRow>
          </Box>
          <Box sx={{ ...DATA_GRID_SX, borderTop: 1, borderColor: "grey.200" }}>
            <ContactBlock
              label="Betreuer"
              person={vertrag.betreuer}
              vertrag={vertrag}
            />
            <ContactBlock
              label="Sachbearbeiter"
              person={vertrag.sachbearbeiter}
              vertrag={vertrag}
            />
          </Box>
        </Card>
        {showClaimsCard && <ContractClaimsCard vertragId={vertrag.id} />}
      </Box>

      {showObjekteTab && (
        <Tabs value={section} onChange={(_, value) => setSection(value)}>
          <Tab value="dokumente" label="Dokumente" />
          <Tab value="objekte" label="Objekte" />
        </Tabs>
      )}

      {section === "objekte" && (
        <ContractObjectsTable
          vertragId={vertrag.id}
          onSelectObjekt={(objekt) => {
            setObjektParam(objekt.id);
            setObjektSelection((count) => count + 1);
            setSection("dokumente");
          }}
        />
      )}

      <Box sx={{ display: section === "objekte" ? "none" : "block" }}>
        <PortalDocumentsTable
          vertragId={vertrag.id}
          title="Dokumente zu diesem Vertrag"
          objekt={objektFilter}
          objektSelection={objektSelection}
          onResetObjekt={() => setObjektParam(null)}
          selectable
        />
      </Box>
    </Stack>
  );
}
